/**
 * GHS 评分环形进度组件。
 *
 * 使用 MUI CircularProgress 实现环形进度，按分数变色：
 * - < 60：红色（error.main）
 * - < 70：橙色（warning.main）
 * - < 80：黄色（info.main，实际用蓝色区分）
 * - >= 80：绿色（success.main）
 */
import { Box, CircularProgress, Typography } from "@mui/material";

export interface GhsRingProps {
  /** GHS 分数（0-100）。 */
  score: number;
  /** 环形直径（px）。 */
  size?: number;
  /** 环形粗细（px）。 */
  thickness?: number;
  /** 是否显示评级文字。 */
  showRating?: boolean;
}

/** 根据分数获取颜色。 */
function getColor(score: number): string {
  if (score >= 80) return "success.main";
  if (score >= 70) return "info.main";
  if (score >= 60) return "warning.main";
  return "error.main";
}

/** 根据分数获取评级。 */
function getRating(score: number): string {
  if (score >= 80) return "优秀";
  if (score >= 70) return "良好";
  if (score >= 60) return "一般";
  if (score >= 40) return "待改进";
  return "较差";
}

export default function GhsRing({
  score,
  size = 80,
  thickness = 6,
  showRating = false,
}: GhsRingProps) {
  const color = getColor(score);
  const rating = getRating(score);

  return (
    <Box
      sx={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: size,
        height: size,
      }}
    >
      <CircularProgress
        variant="determinate"
        value={100}
        size={size}
        thickness={thickness}
        sx={{ color: "divider", position: "absolute" }}
      />
      <CircularProgress
        variant="determinate"
        value={Math.min(score, 100)}
        size={size}
        thickness={thickness}
        sx={{ color, strokeLinecap: "round" }}
      />
      <Box
        sx={{
          position: "absolute",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          sx={{ fontSize: size * 0.28, fontWeight: 700, color, lineHeight: 1 }}
        >
          {score}
        </Typography>
        {showRating && (
          <Typography
            sx={{ fontSize: size * 0.12, color: "text.secondary", mt: 0.3 }}
          >
            {rating}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
